/**
 * Per-field help for the SendGrid (email API) provider fields — same pattern as
 * MetaFieldHelp: an info icon that opens a popover with the URL and the steps to
 * obtain each value in SendGrid. Bilingual (es/en), follows the UI locale.
 */

import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { useLocaleState } from "react-admin";
import { HelpTooltip } from "@/components/common";

export type SendGridHelpField =
  | "apiEndpoint"
  | "apiKey"
  | "from"
  | "customHeaders";

type Locale = "es" | "en";

interface FieldHelp {
  title: string;
  steps: string[];
  url: string;
  note?: string;
}

const HELP: Record<SendGridHelpField, Record<Locale, FieldHelp>> = {
  apiEndpoint: {
    es: {
      title: "API Endpoint",
      steps: [
        "Endpoint de envío de la API de SendGrid.",
        "Valor fijo: https://api.sendgrid.com/v3/mail/send",
        "Solo cámbialo si usas residencia de datos en la UE: https://api.eu.sendgrid.com/v3/mail/send",
      ],
      url: "https://www.twilio.com/docs/sendgrid/api-reference/mail-send/mail-send",
    },
    en: {
      title: "API Endpoint",
      steps: [
        "SendGrid's mail-send API endpoint.",
        "Fixed value: https://api.sendgrid.com/v3/mail/send",
        "Only change it for EU data residency: https://api.eu.sendgrid.com/v3/mail/send",
      ],
      url: "https://www.twilio.com/docs/sendgrid/api-reference/mail-send/mail-send",
    },
  },
  apiKey: {
    es: {
      title: "API Key",
      steps: [
        "En SendGrid, ve a Settings → API Keys.",
        "«Create API Key» → permiso «Full Access» o «Restricted» con «Mail Send» activado.",
        "Copia la clave en ese momento: SendGrid solo la muestra una vez.",
      ],
      note: "En edición, deja el campo vacío para conservar la clave actual.",
      url: "https://app.sendgrid.com/settings/api_keys",
    },
    en: {
      title: "API Key",
      steps: [
        "In SendGrid, go to Settings → API Keys.",
        "«Create API Key» → «Full Access», or «Restricted» with «Mail Send» enabled.",
        "Copy the key right away: SendGrid shows it only once.",
      ],
      note: "On edit, leave this field empty to keep the current key.",
      url: "https://app.sendgrid.com/settings/api_keys",
    },
  },
  from: {
    es: {
      title: "From",
      steps: [
        "Es la dirección remitente. Debe estar VERIFICADA en SendGrid.",
        "SendGrid → Settings → Sender Authentication → verifica un dominio (recomendado) o un «Single Sender».",
        "Usa una dirección de ese dominio o remitente verificado.",
      ],
      url: "https://app.sendgrid.com/settings/sender_auth",
    },
    en: {
      title: "From",
      steps: [
        "The sender address. It must be VERIFIED in SendGrid.",
        "SendGrid → Settings → Sender Authentication → verify a domain (recommended) or a «Single Sender».",
        "Use an address from that verified domain or sender.",
      ],
      url: "https://app.sendgrid.com/settings/sender_auth",
    },
  },
  customHeaders: {
    es: {
      title: "Custom Headers",
      steps: [
        "Opcional. Cabeceras HTTP extra en formato JSON, p. ej. {\"X-My-Header\": \"valor\"}.",
        "No se obtiene de SendGrid: úsalo solo si tu integración lo necesita.",
      ],
      url: "https://www.twilio.com/docs/sendgrid/api-reference/mail-send/mail-send",
    },
    en: {
      title: "Custom Headers",
      steps: [
        "Optional. Extra HTTP headers as JSON, e.g. {\"X-My-Header\": \"value\"}.",
        "Not obtained from SendGrid: use it only if your integration needs it.",
      ],
      url: "https://www.twilio.com/docs/sendgrid/api-reference/mail-send/mail-send",
    },
  },
};

interface SendGridFieldHelpProps {
  field: SendGridHelpField;
}

export const SendGridFieldHelp: React.FC<SendGridFieldHelpProps> = ({ field }) => {
  const [locale] = useLocaleState();
  const loc: Locale = locale === "en" ? "en" : "es";
  const h = HELP[field][loc];
  const linkLabel = loc === "en" ? "Open in SendGrid ↗" : "Abrir en SendGrid ↗";

  return (
    <HelpTooltip
      content={
        <Box sx={{ maxWidth: 360 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {h.title}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
            {h.steps.map((s, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ lineHeight: 1.5, wordBreak: "break-word" }}
              >
                {s}
              </Typography>
            ))}
          </Box>
          {h.note && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, lineHeight: 1.5 }}
            >
              {h.note}
            </Typography>
          )}
          <Link
            href={h.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: "inline-block", mt: 1.25, wordBreak: "break-all" }}
          >
            {linkLabel}
          </Link>
        </Box>
      }
    />
  );
};

export default SendGridFieldHelp;
